<template>
  <div id="companyVehicleForm" class="form">
    <v-form v-if="loaded" ref="form" v-model="valid" lazy-validation>
      <v-text-field
        v-model="companyVehicle.vehicleLicensePlate"
        label="License Plate"
        :rules="[(v) => !!v || 'License Plate is required']"
        required
      />

      <v-select
        v-model="companyVehicle.vehicleType"
        :items="companyVehicle.relations.vehicleType"
        label="Type"
        chips
        deletable-chips
      ></v-select>
      <v-text-field
        v-model="companyVehicle.vehicleNotes"
        label="Notes"
        :required="false"
      />
      <!--
        Type to search: the list comes from the server, not from a preloaded copy of
        every company. `no-filter` hands filtering to the backend - Vuetify must not
        also filter, or it would filter the already-filtered page.
      -->
      <v-autocomplete
        v-model="companyVehicle.companyId"
        :items="companyVehicle.relations.company"
        :search-input.sync="companySearch"
        :loading="companyLoading"
        no-filter
        label="Company"
        hint="Type part of the company name to search"
        persistent-hint
        :multiple="false"
        item-text="label"
        item-value="id"
        :chips="false"
        deletable-chips
        :rules="[(v) => !!v || 'Company is required']"
        required
      ></v-autocomplete>
      <div class="mt-4 d-flex justify-space-between">
        <v-btn @click="goBack"> Back </v-btn>
        <v-btn @click="handleSubmit" color="primary">
          {{ id ? "Update" : "Save" }}
        </v-btn>
        <span></span>
      </div>
    </v-form>
  </div>
</template>

<script>
import { getCompanyVehicle } from "@/services/companyVehicle";
import { getAllCompanies, getCompany } from "@/services/company";
import { mapActions } from "vuex";

// One page of matches is all a person reads before typing another letter. The old
// code asked for 10000 and the server capped it at 200, so 251 of 451 companies were
// simply unreachable from this form - see docs/VEHICLE-CREATE-OUTAGE-2026-09-16.md.
const COMPANY_PAGE_SIZE = 50;

export default {
  props: ["id"],
  data() {
    return {
      loaded: false,
      valid: false,
      companySearch: null,
      companyLoading: false,
      companySearchToken: 0,
      companySearchTimer: null,
      companyVehicle: {
        relations: {
          vehicleType: ["TRUCK", "SEDAN", "SUV", "VAN"],
          company: [],
        },
        vehicleLicensePlate: "",
        vehicleType: "TRUCK",
        vehicleNotes: "",
        companyId: null,
      },
    };
  },
  created() {
    this.setInstance();
  },
  watch: {
    companySearch(query, previous) {
      // Vuetify sets this to the selected item's text right after a pick; searching
      // for it again would be a pointless round trip.
      if (this.companyIsAlreadySelected(query)) {
        return;
      }

      // Closing the menu (Escape, blur, selecting) resets search-input to null. That
      // is not the user asking for "all companies" - acting on it fires an unfiltered
      // search whose reply lands last and replaces the matches the user was reading.
      // Only treat an empty query as a real request when the user cleared a box that
      // had something in it.
      if (!query && !previous) {
        return;
      }

      // One request per pause, not one per keystroke: Thai input in particular emits a
      // character at a time, and each in-flight reply could otherwise overwrite a later
      // one's results.
      clearTimeout(this.companySearchTimer);
      this.companySearchTimer = setTimeout(() => this.searchCompanies(query), 250);
    },
  },
  beforeDestroy() {
    clearTimeout(this.companySearchTimer);
  },
  methods: {
    ...mapActions("companyVehicle", [
      "createCompanyVehicle",
      "updateCompanyVehicle",
    ]),
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),

    async handleSubmit() {
      // Vuetify's `required` prop only draws the asterisk - it does not validate.
      // Without this call a blank Company submitted, and company_vehicle.company_id
      // is NOT NULL, so the insert failed at the database with a message the user
      // never saw. Same guard as CompanyForm.vue.
      if (!this.$refs.form.validate()) {
        return;
      }

      if (this.id) {
        let companyVehicle = this.companyVehicle;
        delete companyVehicle.relations;

        this.updateCompanyVehicle(companyVehicle)
          .then((response) => {
            console.log(response);
            this.addSuccessMessage("CompanyVehicle was updated successfully");
            this.goBack();
          })
          .catch((error) => {
            this.addErrorMessage(this.serverError(error, "updating"));
          });
      } else {
        let companyVehicle = this.companyVehicle;
        delete companyVehicle.relations;

        this.createCompanyVehicle(companyVehicle)
          .then((response) => {
            console.log(response);
            this.addSuccessMessage("CompanyVehicle was created successfully");
            this.goBack();
          })
          .catch((error) => {
            this.addErrorMessage(this.serverError(error, "creating"));
          });
      }
    },
    // The server explains itself (duplicate plate, missing company, ...); the old
    // catch blocks threw that away and showed a fixed string instead.
    serverError(error, verb) {
      return error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : `Error ${verb} CompanyVehicle, please try again later`;
    },
    goBack() {
      this.$router.go(-1);
    },
    setInstance() {
      if (this.id) {
        this.companyVehicle.id = this.id;
        getCompanyVehicle(this.id).then((response) => {
          let instance = response.data;
          for (let property in instance) {
            if (
              Object.prototype.hasOwnProperty.call(instance, property) &&
              Object.prototype.hasOwnProperty.call(
                this.companyVehicle,
                property
              )
            ) {
              this.companyVehicle[property] = instance[property];
            }
          }
          // The vehicle's own company may not be in the first page of matches, so
          // fetch it by id - otherwise Edit would open with the Company box blank
          // and the user would have to find it again.
          this.ensureSelectedCompanyPresent(this.companyVehicle.companyId);
          this.loaded = true;
        });
      } else {
        this.loaded = true;
      }
      this.setDependencies();
    },
    /**
     * Seed the dropdown with a first page. The rest arrives as the user types.
     *
     * This used to ask for size=10000 and assume it got everything. Since the server
     * caps a page at max-page-size it silently returned the first 200 of 451 rows,
     * and this form only ever reads page 0 - so any company sorting past #200 could
     * not be chosen at all. Thai-named companies sort last, which is why the bug
     * presented as "cannot add a Thai supplier's vehicle".
     */
    setDependencies() {
      this.searchCompanies(null);
    },

    /**
     * The text shown for a company. Vuetify needs `item-text` to be a KEY, not a
     * function: with a function it cannot resolve the label it should leave in the box,
     * and combined with no-filter it clears search-input on every keystroke - which is
     * why typing produced an unfiltered request instead of a search. Every other
     * autocomplete in this app passes a plain key; this one is now the same.
     */
    companyLabel(company) {
      return `${company.name} [${company.type || "Unknown"}]`;
    },

    withLabels(companies) {
      return (companies || []).map((c) => ({ ...c, label: this.companyLabel(c) }));
    },

    companyIsAlreadySelected(query) {
      if (!query) {
        return false;
      }
      return (this.companyVehicle.relations.company || []).some(
        (c) => (c.label || this.companyLabel(c)) === query
      );
    },

    /**
     * Ask the server for companies matching `query`.
     *
     * Responses are matched against a token so a slow reply for an earlier keystroke
     * cannot overwrite the list belonging to a later one.
     */
    searchCompanies(query) {
      const token = ++this.companySearchToken;
      const params = { page: 0, size: COMPANY_PAGE_SIZE, sort: "name" };
      if (query) {
        params.name = query;
      }

      this.companyLoading = true;
      return getAllCompanies(params)
        .then((response) => {
          if (token !== this.companySearchToken) {
            return;
          }
          this.mergeCompanies(this.withLabels(response.data.content));
        })
        .catch(() => {
          if (token === this.companySearchToken) {
            this.addErrorMessage("Could not load the company list");
          }
        })
        .finally(() => {
          if (token === this.companySearchToken) {
            this.companyLoading = false;
          }
        });
    },

    /**
     * Replace the visible options, but never drop the currently selected company:
     * v-autocomplete renders the selection from `items`, so if a search result does
     * not contain it the field would appear to empty itself while still holding the id.
     */
    mergeCompanies(found) {
      const selectedId = this.companyVehicle.companyId;
      if (!selectedId) {
        this.companyVehicle.relations.company = found;
        return;
      }
      const selected = (this.companyVehicle.relations.company || []).find(
        (c) => c.id === selectedId
      );
      this.companyVehicle.relations.company =
        selected && !found.some((c) => c.id === selectedId)
          ? [selected, ...found]
          : found;
    },

    /**
     * On the Edit screen the vehicle names a company that a blank search need not
     * return, so fetch that one row by id and keep it in the list.
     */
    ensureSelectedCompanyPresent(companyId) {
      if (!companyId) {
        return Promise.resolve();
      }
      return getCompany(companyId)
        .then((response) => {
          const company = response.data;
          if (!company) {
            return;
          }
          const list = this.companyVehicle.relations.company || [];
          if (!list.some((c) => c.id === company.id)) {
            this.companyVehicle.relations.company = [
              { ...company, label: this.companyLabel(company) },
              ...list,
            ];
          }
        })
        .catch(() => {
          // A company that cannot be read is not worth blocking the form for; the
          // field simply shows no text and the required rule still applies.
        });
    },
  },
  components: {},
};
</script>